import type { SongRequest } from '../services/requests';
import { isQueuedInPlayer } from './playerQueueSync';

export type LateQueueStage = 'caution' | 'warning' | 'strong';
export const LATE_QUEUE_CAUTION_MS = 45_000;
export const LATE_QUEUE_WARNING_MS = 30_000;
export const LATE_QUEUE_STRONG_MS = 20_000;
const TAKEOVER_MS = 45_000;
const REMINDER_MS = 30_000;

export function lateQueueStage(remainingMs: number | null): LateQueueStage | null {
  if (remainingMs === null || remainingMs > LATE_QUEUE_CAUTION_MS) return null;
  if (remainingMs <= LATE_QUEUE_STRONG_MS) return 'strong';
  if (remainingMs <= LATE_QUEUE_WARNING_MS) return 'warning';
  return 'caution';
}

// Other songs are queued in the player but the next one isn't (ITER-183): queuing it now can upset the player
export function lateQueueApplies(input: {
  level: 'bridge' | 'checklist';
  manual: boolean;
  nextSong: Pick<SongRequest, 'queuedInPlayer' | 'queuedInSpotify'> | null;
  queuedInPlayerCount: number;
}): boolean {
  const { level, manual, nextSong, queuedInPlayerCount } = input;
  return level === 'bridge' && manual && nextSong !== null && !isQueuedInPlayer(nextSong) && queuedInPlayerCount > 0;
}

// The risk is reordering the player's queue near a transition, not adding to it (owner, 2026-10-07)
export function lateQueueMessage(stage: LateQueueStage, remainingMs: number, playerLabel: string): string {
  if (stage === 'caution') {
    const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
    return `Moved ahead of songs already in ${playerLabel}'s queue. Song ends in ${seconds}s: queue it and move it up quickly, or wait for the next song.`;
  }
  return `If you queue it now, leave it where it lands in ${playerLabel}'s queue. Moving it up this close to the end can disrupt playback.`;
}

export function formatTimeLeft(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
}

export type SongEndBanner = 'takeover' | 'reminder' | null;

// The red 45 s takeover and the amber 30 s reminder never show together, and neither shows in the late-queue case
// or while the DJ has marked this song as the last one (ITER-175)
export function songEndBanner(input: {
  countdownMs: number | null;
  queueEmpty: boolean;
  hasNextSong: boolean;
  nextQueued: boolean;
  lateQueue: boolean;
  warn45Dismissed: boolean;
  warn30Dismissed: boolean;
  lastSongActive: boolean;
}): SongEndBanner {
  const { countdownMs, queueEmpty, hasNextSong, nextQueued, lateQueue, warn45Dismissed, warn30Dismissed, lastSongActive } = input;
  if (lastSongActive || countdownMs === null) return null;

  const nextNotQueued = !queueEmpty && hasNextSong && !nextQueued && !lateQueue;
  if (countdownMs < TAKEOVER_MS && !warn45Dismissed && (queueEmpty || nextNotQueued)) return 'takeover';

  const reminder = countdownMs < REMINDER_MS && countdownMs > 0 && !queueEmpty && hasNextSong && !lateQueue
    && (nextQueued || warn45Dismissed) && !warn30Dismissed;
  return reminder ? 'reminder' : null;
}
